import React from 'react';
import { motion } from 'framer-motion';
import { RevealItem, revealContainer } from '../common/Reveal';
import { PageHeader } from '../layout/PageHeader';
import { TeamSelect } from '../common/TeamSelect';
import { SegmentedControl } from '../common/SegmentedControl';
import { SearchInput } from '../common/SearchInput';
import { SourceFilter } from '../common/SourceFilter';
import { EmptyState } from '../common/EmptyState';
import { Pagination } from '../common/Pagination';
import { DeltaChip } from '../common/DeltaChip';
import { StateLegend } from '../floor/StateLegend';
import { SortSelect } from '../users/SortSelect';
import { RosterGroup, type RosterMetric } from '../users/RosterGroup';
import { UserList } from '../users/UserList';
import { PAGE_SIZE, useUsersDirectory, type RosterGroupData, type UsersGrouping, type UsersLayout } from '../users/useUsersDirectory';
import { answerRateTone, formatDuration, formatNumber, formatPercent, formatSignedPts, initials } from '../../utils/format';
import { scopeLabels } from '../../utils/range';
import { sourceLabel } from '../../utils/sources';
import { teamTone } from '../../utils/teams';
import { dateLabel } from '../../utils/floor';
import type { VoiceSource } from '../../types/voip';
import type { CallsInsightHubState } from '../CallsInsightHub/useCallsInsightHub';
const voiceSources: VoiceSource[] = ['voip', 'five9'];
const layoutOptions: {
  value: UsersLayout;
  label: string;
}[] = [{
  value: 'team',
  label: 'Team view'
}, {
  value: 'list',
  label: 'List view'
}];
const groupingOptions: {
  value: UsersGrouping;
  label: string;
}[] = [{
  value: 'team',
  label: 'Team'
}, {
  value: 'tier',
  label: 'Tier'
}];
function focusMetric(g: RosterGroupData): RosterMetric {
  const diff = g.floor.focus - g.prevFloor.focus;
  return {
    label: 'Focus',
    value: formatPercent(g.floor.focus),
    delta: <DeltaChip value={diff} text={formatSignedPts(diff)} className="text-[11px]" data-id="element-817" data-mp="1c4h2z4mxabgj" />
  };
}
function teamMetrics(g: RosterGroupData): RosterMetric[] {
  return [{
    label: 'Talk time',
    value: formatDuration(g.stats.talkSeconds)
  }, {
    label: 'Answer rate',
    value: formatPercent(g.stats.answerRate),
    tone: answerRateTone(g.stats.answerRate)
  }, {
    label: 'Calls',
    value: formatNumber(g.stats.totalCalls)
  }, {
    label: 'Avg call',
    value: formatDuration(g.stats.avgSeconds)
  }, focusMetric(g)];
}
function tierMetrics(g: RosterGroupData): RosterMetric[] {
  return [{
    label: 'Avg score',
    value: g.floor.score.toFixed(1)
  }, focusMetric(g), {
    label: 'Leads converted',
    value: formatNumber(g.floor.leads)
  }, {
    label: 'Loans submitted',
    value: formatNumber(g.floor.loans)
  }, {
    label: 'Talk time',
    value: formatDuration(g.stats.talkSeconds)
  }];
}
function GroupBadge({
  group
}: {
  group: RosterGroupData;
}) {
  const base = 'flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[12px] font-bold';
  if (group.tier) {
    return <span className={`${base} ${group.tier.badge}`} aria-hidden data-id="element-818" data-mp="k5c5u1e172u6">
        {group.tier.min > 0 ? `${group.tier.min}+` : '<45'}
      </span>;
  }
  return <span className={`${base} ${group.team ? teamTone[group.team] : 'bg-accent-soft text-accent'}`} aria-hidden data-id="element-819" data-mp="30q7ge7751jq6">
      {initials(group.title)}
    </span>;
}
export function UsersView({
  hub
}: {
  hub: CallsInsightHubState;
}) {
  const dir = useUsersDirectory(hub);
  const isTeam = dir.layout === 'team';
  const count = dir.searching ? `${dir.shownUsers} of ${dir.totalUsers}` : `${dir.totalUsers}`;
  const groupedLabel = dir.grouping === 'tier' ? 'Grouped by production tier' : 'Grouped by team';
  const floorDay = dateLabel(hub.floorDate);
  return <>
      <PageHeader title="Users" subtitle={`${count} users · ${isTeam ? groupedLabel : 'All teams'}`} data-id="element-820" data-mp="2qst1ic0v52zz" />

      <RevealItem className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center" data-id="element-821" data-mp="1tdowkelp9vrm">
        <div className="flex flex-wrap items-center gap-3" data-id="element-822" data-mp="1tbksnwnswwrc">
          <SourceFilter<VoiceSource> value={hub.voiceSource} onChange={hub.setSource} include={voiceSources} data-id="element-823" data-mp="1yb75w63mqd05" />
          <SearchInput value={dir.query} onChange={dir.setQuery} placeholder="Search name, extension, or team..." label="Search users and teams" className="sm:w-80" data-id="element-824" data-mp="4u17yd10y4jj" />
          <TeamSelect value={hub.team} onChange={hub.setTeam} data-id="element-825" data-mp="8vebuhkk3xnt" />
          <SortSelect value={dir.sort} onChange={dir.setSort} data-id="element-826" data-mp="8u3cqv223e31" />
        </div>
        <div className="lg:ml-auto" data-id="element-827" data-mp="nizfqdp9a0ws">
          <SegmentedControl label="Layout" options={layoutOptions} value={dir.layout} onChange={dir.setLayout} data-id="element-828" data-mp="nm3sw0kp86zl" />
        </div>
      </RevealItem>

      <RevealItem className="mb-3 flex flex-wrap items-center justify-between gap-3" data-id="element-829" data-mp="wl8rf6w9g0br">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2" data-id="element-830" data-mp="siu9cp0f14lj">
          {isTeam && <span className="flex items-center gap-2 text-xs text-muted" data-id="element-831" data-mp="2ybsi69wxwgfn">
              Group by
              <SegmentedControl label="Group roster by" options={groupingOptions} value={dir.grouping} onChange={dir.setGrouping} data-id="element-832" data-mp="2ub8mzbvxfmw5" />
            </span>}
          <p className="text-xs text-subtle" data-id="element-833" data-mp="2k8wljudz2n6v">
            {sourceLabel(hub.voiceSource)} calls, {scopeLabels[hub.range]} · Five9 state, {floorDay}
          </p>
          <StateLegend data-id="element-834" data-mp="2afxmtjorhg9j" />
        </div>
        {isTeam && dir.groups.length > 1 && <button type="button" onClick={dir.toggleAll} className="rounded-md text-xs font-medium text-muted transition-colors duration-150 ease-out hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-accent" data-id="element-835" data-mp="2ffa7bxs5ub8y">
            {dir.allExpanded ? 'Collapse all' : 'Expand all'}
          </button>}
      </RevealItem>

      {dir.groups.length === 0 ? <EmptyState description="Try a different name, extension, or team." data-id="element-836" data-mp="1wi0p2jykn3ta" /> : isTeam ? <motion.div key={`roster-${dir.grouping}`} variants={revealContainer} initial="hidden" animate="show" className="flex flex-col gap-3" data-id="element-837" data-mp="3lxgrlghzpra7">
          {dir.groups.map(g => {
        const team = g.team;
        const canViewTeam = dir.grouping === 'team' && !!team && hub.team === 'All' && !dir.searching;
        const matchNote = dir.searching && g.users.length < g.members ? ` · ${g.users.length} ${g.users.length === 1 ? 'match' : 'matches'}` : '';
        return <RevealItem key={g.id} data-id="element-838" data-mp="3ai411y6pu4wx">
                <RosterGroup id={g.id} title={g.title} countLabel={`${g.members} users${matchNote}`} badge={<GroupBadge group={g} data-id="element-840" data-mp="71ewuiulv7af" />} metrics={g.tier ? tierMetrics(g) : teamMetrics(g)} users={g.users} expanded={dir.isExpanded(g.id)} showingAll={dir.isShowingAll(g.id)} searching={dir.searching} matchedIds={dir.matchedIds} floorById={hub.floorById} onToggle={dir.toggleGroup} onShowAll={dir.setGroupShowAll} onViewAll={canViewTeam && team ? () => dir.viewTeam(team) : undefined} onSelect={hub.setSelectedId} data-id="element-839" data-mp="3aj5g0dqurbjx" />
              </RevealItem>;
      })}
        </motion.div> : <RevealItem key="list" data-id="element-841" data-mp="176z43ezc2ssl">
          <UserList rows={dir.pageRows} floorById={hub.floorById} onSelect={hub.setSelectedId} footer={<Pagination page={dir.page} pageCount={dir.pageCount} total={dir.listRows.length} pageSize={PAGE_SIZE} onChange={dir.setPage} data-id="element-843" data-mp="1g8fcjh6l8ku6" />} data-id="element-842" data-mp="1c7d9ep9jvomx" />
        </RevealItem>}
    </>;
}